import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Users, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent, Badge } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { productionService } from '@services/productionService';
import { ProductionEmployee } from '@/types/production';

/**
 * Listagem de colaboradores de produção (atelier de cabides)
 */
export const EmployeeListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<ProductionEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Exclusão
  const [deleteTarget, setDeleteTarget] = useState<ProductionEmployee | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await productionService.listEmployees({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadItems();
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await productionService.deleteEmployee(deleteTarget.id);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir colaborador');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Colaboradores de Produção</h1>
          <p className="text-slate-600">Gerencie os colaboradores do atelier de produção</p>
        </div>
        <PermissionGuard permission="production.create">
          <Button onClick={() => navigate('/production/employees/new')} icon={<Plus size={20} />}>
            Novo Colaborador
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros e busca */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={<Search size={20} />}
              />
            </div>
            <div>
              <select
                className="input"
                value={statusFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                }}
              >
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
                <option value="all">Todos</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando colaboradores...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search ? 'Nenhum colaborador encontrado' : 'Nenhum colaborador cadastrado'}
              </p>
              <PermissionGuard permission="production.create">
                <Button onClick={() => navigate('/production/employees/new')}>
                  Cadastrar primeiro colaborador
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Nome</th>
                  <th className="text-left">Observações</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <p className="font-medium text-slate-900">{item.name}</p>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {item.notes ? (
                          item.notes.length > 80 ? `${item.notes.slice(0, 80)}...` : item.notes
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={item.is_active ? 'success' : 'danger'}>
                        {item.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="production.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/production/employees/${item.id}/edit`)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="production.delete">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteTarget(item)}
                            icon={<Trash2 size={14} />}
                          >
                            Excluir
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Colaborador"
        message={`Tem certeza que deseja excluir o colaborador "${deleteTarget?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleting}
      />
    </div>
  );
};
